/**
 * The type-size and line-spacing control.
 *
 * Lives behind a single icon rather than on the toolbar: it is adjusted once at
 * the start of a session and then never again, so permanent screen space would
 * be a poor trade.
 */

import { Type } from "lucide-react";
import {
  LINE_SPACINGS,
  TEXT_SIZES,
  useReading,
  type LineSpacing,
  type TextSize,
} from "../reading.tsx";
import { Button } from "./ui/button.tsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover.tsx";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip.tsx";

export function ReadingControls() {
  const { size, spacing, setSize, setSpacing, reset, isDefault } = useReading();

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Reading settings"
              className="text-muted-foreground hover:text-foreground"
            >
              <Type />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Text size and spacing</TooltipContent>
      </Tooltip>

      <PopoverContent align="end" className="w-64 font-sans">
        <div className="grid gap-4">
          <Field label="Text size">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={size}
              onValueChange={(next) => next && setSize(next as TextSize)}
              aria-label="Text size"
              className="w-full"
            >
              {TEXT_SIZES.map((option) => (
                <ToggleGroupItem
                  key={option.id}
                  value={option.id}
                  aria-label={option.label}
                  className="flex-1 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  {/* The letter is sized to the option, so the control shows
                      what it does rather than describing it. */}
                  <span style={{ fontSize: `${option.scale * 0.8}rem` }}>A</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Field>

          <Field label="Line spacing">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={spacing}
              onValueChange={(next) => next && setSpacing(next as LineSpacing)}
              aria-label="Line spacing"
              className="w-full"
            >
              {LINE_SPACINGS.map((option) => (
                <ToggleGroupItem
                  key={option.id}
                  value={option.id}
                  className="flex-1 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Field>

          {!isDefault && (
            <Button
              variant="ghost"
              size="sm"
              onClick={reset}
              className="justify-self-start px-0 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
              Reset to default
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <span className="text-xs font-semibold tracking-[0.06em] text-muted-foreground uppercase">
        {label}
      </span>
      {children}
    </div>
  );
}
